import BillingHistory from "@/components/billing-history";
import EnterpriseCard from "@/components/enterprise-card";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePlanAndCredits } from "@/hooks/use-plan-and-credits";
import { useSubscription } from "@/hooks/use-subscription";
import BillingOverview from "@/pages/billing/components/overview";
import Plans from "@/pages/billing/components/plans";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, Loader2 } from "lucide-react";

export const Route = createFileRoute("/dashboard/billing/")({
  component: BillingPage,
});

function BillingPage() {
  const {
    subscription,
    subscriptions,
    refetch: refetchSubscription,
  } = useSubscription();

  // Use the new hook
  const state = usePlanAndCredits();
  const {
    plans,
    plansLoading,
    plansError,
    loading: planCreditsLoading,
    error: planCreditsError,
    refetchPlans,
    refetchCredits,
  } = state;

  // Mark current plan based on user's subscription
  const plansWithCurrent = plans.map((plan) => ({
    ...plan,
    current:
      subscriptions?.some(
        (sub) =>
          sub.status === "active" &&
          sub.items?.data?.[0]?.price?.id === plan.priceId
      ) || subscription?.items?.[0]?.price?.id === plan.priceId,
  }));

  const loading = plansLoading || planCreditsLoading;

  return (
    <ProtectedRoute>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Billing</h1>
          <p className="text-gray-600 mt-2">
            Manage your subscription and billing information
          </p>
        </div>

        {loading && !subscriptions?.length ? null : (
          <BillingOverview state={state} subscriptions={subscriptions} />
        )}

        {/* Plan Selection */}
        {plansLoading && !plansWithCurrent?.length ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-2">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span className="text-gray-700">Loading plans...</span>
              </div>
            </CardContent>
          </Card>
        ) : plansError ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Failed to load plans
                  </h3>
                  <p className="text-gray-500 mb-4">{plansError}</p>
                  <Button
                    onClick={() => {
                      refetchPlans();
                      refetchSubscription();
                    }}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    Try again
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Plans
            plans={plansWithCurrent}
            refetchCredits={refetchCredits}
            refetchSubscription={refetchSubscription}
          />
        )}

        {/* Enterprise */}
        <EnterpriseCard />

        {/* Billing History */}
        <BillingHistory />
      </div>
    </ProtectedRoute>
  );
}
